import { createFileRoute, Link, useNavigate, useLocation, Outlet } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { useParentGate } from "@/hooks/use-parent-gate";
import { Home, BarChart3, Sparkles, LogOut, Settings as SettingsIcon, Languages, Lock } from "lucide-react";
import { BetaFeedback } from "@/components/BetaFeedback";

export const Route = createFileRoute("/_authenticated/app")({
  component: AppLayout,
});

function AppLayout() {
  const { signOut } = useAuth();
  const { toggle, lang, t, dir } = useLang();
  const { ensureUnlocked, hasPin, unlocked, lock } = useParentGate();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (p: string) => (p === "/app" ? pathname === "/app" : pathname.startsWith(p));

  const gotoGated = async (to: "/app/parent" | "/app/pricing" | "/app/settings") => {
    const ok = await ensureUnlocked();
    if (ok) navigate({ to });
  };

  return (
    <div dir={dir} className="min-h-screen bg-background flex flex-col pb-24">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-clay/30 px-5 py-3 flex items-center justify-between">
        <Link to="/app" className="flex items-baseline gap-2">
          <span className="font-display-ar text-xl font-black text-primary">جذوري</span>
          <span className="font-display-en text-base font-extrabold tracking-tight">JOZORI</span>
        </Link>
        <div className="flex items-center gap-2">
          {hasPin && unlocked && (
            <button
              onClick={lock}
              aria-label="Lock parent zone"
              className="size-9 rounded-full bg-primary/15 text-primary flex items-center justify-center hover:bg-primary/25 transition-colors"
              title="Lock"
            >
              <Lock className="size-4" />
            </button>
          )}
          <button
            onClick={toggle}
            aria-label="Toggle language"
            className="h-9 px-3 rounded-full bg-clay/30 flex items-center gap-1.5 hover:bg-clay/50 transition-colors text-xs font-bold"
          >
            <Languages className="size-3.5" />
            {lang === "ar" ? "EN" : "ع"}
          </button>
          <button
            onClick={async () => {
              await signOut();
              navigate({ to: "/" });
            }}
            aria-label={t("signOut")}
            className="size-9 rounded-full bg-clay/30 flex items-center justify-center hover:bg-clay/50 transition-colors"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <BetaFeedback />

      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-clay/40 px-3 py-2">
        <div className="max-w-md mx-auto grid grid-cols-4 gap-1">
          <TabLink to="/app" icon={<Home className="size-5" />} label={t("kids")} active={isActive("/app")} />
          <TabButton
            onClick={() => gotoGated("/app/parent")}
            icon={<BarChart3 className="size-5" />}
            label={t("parent")}
            active={isActive("/app/parent")}
            locked={hasPin === true && !unlocked}
          />
          <TabButton
            onClick={() => gotoGated("/app/pricing")}
            icon={<Sparkles className="size-5" />}
            label={t("plan")}
            active={isActive("/app/pricing")}
            locked={hasPin === true && !unlocked}
          />
          <TabButton
            onClick={() => gotoGated("/app/settings")}
            icon={<SettingsIcon className="size-5" />}
            label={t("settings")}
            active={isActive("/app/settings")}
            locked={hasPin === true && !unlocked}
          />
        </div>
      </nav>
    </div>
  );
}

function TabButton({
  onClick,
  icon,
  label,
  active,
  locked,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  active: boolean;
  locked: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center gap-1 py-2 rounded-2xl transition-colors ${
        active ? "text-primary" : "text-foreground/50"
      }`}
    >
      {icon}
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      {locked && (
        <span className="absolute top-1 right-3 size-3.5 rounded-full bg-card border border-clay/50 flex items-center justify-center">
          <Lock className="size-2 text-foreground/60" />
        </span>
      )}
    </button>
  );
}

function TabLink({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center gap-1 py-2 rounded-2xl transition-colors ${
        active ? "text-primary" : "text-foreground/50"
      }`}
    >
      {icon}
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
    </Link>
  );
}
